
import { Component, forwardRef, OnDestroy } from '@angular/core';
import {
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    NonNullableFormBuilder,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';
import { ToneSet } from '../../models/branding.model';
import { ToneSelectorFormGroup, ToneSelectorFormGroupType } from '../../models/tone-selector-form.model';

@Component({
    selector: 'app-tone-selector-form',
    imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatIconModule
],
    templateUrl: './tone-selector-form.component.html',
    styleUrls: ['./tone-selector-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ToneSelectorFormComponent),
            multi: true,
        },
    ]
})
export class ToneSelectorFormComponent implements ControlValueAccessor, OnDestroy {
    public tonesList = [100, 99, 98, 95, 90, 80, 70, 60, 50, 40, 35, 30, 25, 20, 15, 10, 5, 0];
    public form: ToneSelectorFormGroup;
    private unsubscribe$ = new Subject<void>();
    private onChange: (value: ToneSet) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private fb: NonNullableFormBuilder) {
        this.createForm();
    }

    createForm(): void {
        this.form = this.fb.group<ToneSelectorFormGroupType>({
            lightTone: this.fb.control(95),
            primaryTone: this.fb.control(40),
            darkTone: this.fb.control(20),
        });

        this.form.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
            this.onChange(value as ToneSet);
            this.onTouched();
        });
    }

    writeValue(value: ToneSet): void {
        if (value) {
            this.form.patchValue(value, { emitEvent: false });
        }
    }

    registerOnChange(fn: (value: ToneSet) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled ? this.form.disable() : this.form.enable();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
